/* pwt-gift-card.js - perfect gift card wizard (sections/pwt-gift-card.liquid).
   Gift Card Experience design, Sep 2026.

   One question at a time: who is it for, how much, which design, then (only when
   sending) names, how to send it, a message and when. Choice questions advance on
   their own after a beat; typed ones have a next. Every answer folds into a row
   with "change", the gallery picks up the artwork, amount and name as you go, and
   the last step is a card you turn over. Only three things can be wrong: their
   email when email is chosen, their mobile when text is chosen, and a day inside
   the scheduling window.

   Add to bag goes through window.NativeCart (assets/native-cart-drawer.js) so the
   drawer opens; there is no product form, so an Enter keypress can never submit a
   half-finished card. Line item property names are the Full Story Utilities data
   contract (see the section header) - keep them in sync with the app. */
(function () {
  'use strict';

  var ROOT_SEL = '[data-pwt-gc]';
  var LABELS = { who: 'for', amount: 'amount', design: 'design', names: 'names', method: 'send by', message: 'message', when: 'when' };
  var MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  var DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var MESSAGE_MAX = 300;
  var ADVANCE_MS = 320;

  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function hasKeys(o) { for (var k in o) { if (o.hasOwnProperty(k) && o[k]) return true; } return false; }
  function trim(v) { return String(v || '').replace(/^\s+|\s+$/g, ''); }

  function init(root) {
    if (root.__gcBooted) return;
    var cfgEl = root.querySelector('[data-gc-config]');
    if (!cfgEl) return;
    var CFG;
    try { CFG = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    root.__gcBooted = true;

    /* ---------------- config ---------------- */

    var TZ = CFG.tz || 'America/Los_Angeles';
    try { new Intl.DateTimeFormat('en-US', { timeZone: TZ }); } catch (e) { TZ = 'America/Los_Angeles'; }
    var TZ_LABEL = CFG.tzLabel || 'pacific';
    var MAX_DAYS = Math.min(365, Math.max(1, parseInt(CFG.maxDays, 10) || 90));
    var SLOTS = String(CFG.slots || '7,9,12,15,18').split(',')
      .map(function (s) { return parseInt(s, 10); })
      .filter(function (h, i, a) { return h >= 0 && h <= 23 && a.indexOf(h) === i; })
      .sort(function (a, b) { return a - b; });
    if (!SLOTS.length) SLOTS = [7, 9, 12, 15, 18];
    var DEFAULT_SLOT = SLOTS.indexOf(9) !== -1 ? 9 : SLOTS[0];
    var AMOUNTS = CFG.amounts || [];
    var DESIGNS = CFG.designs || [];
    var VARIANTS = CFG.variants || [];
    var SELF_NOTE = CFG.selfNote || 'a little something for yourself.';
    var OFFER_TEXT = CFG.offerText !== false;

    /* ---------------- elements ---------------- */

    function q(sel) { return root.querySelector(sel); }
    function qa(sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

    var el = {
      price: q('[data-gc-price]'),
      count: q('[data-gc-count]'),
      progress: q('[data-gc-progress]'),
      bar: q('[data-gc-progress-bar]'),
      arts: qa('[data-art]'),
      chipFor: q('[data-gc-chip-for]'),
      chipAmount: q('[data-gc-chip-amount]'),
      steps: qa('[data-step]'),
      counter: q('[data-gc-counter]'),
      later: q('[data-gc-later]'),
      slots: q('[data-gc-slots]'),
      sends: q('[data-gc-sends]'),
      flip: q('[data-gc-flip]'),
      flipHint: q('[data-gc-flip-hint]'),
      reviewArt: q('[data-gc-review-art]'),
      reviewFor: q('[data-gc-review-for]'),
      reviewAmount: q('[data-gc-review-amount]'),
      backFor: q('[data-gc-back-for]'),
      backMsg: q('[data-gc-back-msg]'),
      backFrom: q('[data-gc-back-from]'),
      backAmount: q('[data-gc-back-amount]'),
      backSends: q('[data-gc-back-sends]'),
      reviewSends: q('[data-gc-review-sends]'),
      cta: q('[data-gc-cta]'),
      addError: q('[data-gc-add-error]'),
      adds: qa('[data-gc-add]'),
      sticky: q('[data-gc-sticky]'),
      stickyArt: q('[data-gc-sticky-art]'),
      stickySub: q('[data-gc-sticky-sub]')
    };
    var PRICE_RANGE = el.price ? el.price.textContent : '';
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var finePointer = window.matchMedia && window.matchMedia('(pointer: fine)').matches;

    /* ---------------- state ---------------- */

    var S = {
      who: null, amount: null, design: null,
      from: '', to: '', method: 'email', email: '', mobile: '', message: '',
      when: null, date: '', slot: DEFAULT_SLOT,
      answered: {}, active: 'who', errors: {}, flipped: false, turned: false
    };
    var pre = CFG.preselect || {};
    if (pre.amount && amountBy(pre.amount)) S.amount = pre.amount;
    if (pre.design && designBy(pre.design)) S.design = pre.design;

    var advTimer = null;
    var busy = false;
    var ctaInView = true;

    /* ---------------- lookups ---------------- */

    function amountBy(v) { for (var i = 0; i < AMOUNTS.length; i++) { if (AMOUNTS[i].value === v) return AMOUNTS[i]; } return null; }
    function designBy(v) { for (var i = 0; i < DESIGNS.length; i++) { if (DESIGNS[i].value === v) return DESIGNS[i]; } return null; }
    function variantFor(a, d) {
      for (var i = 0; i < VARIANTS.length; i++) {
        var v = VARIANTS[i];
        if (v.amount === a && (!DESIGNS.length || v.design === d)) return v;
      }
      return null;
    }
    function heroDesign() { return designBy(S.design) || designBy(CFG.startDesign) || DESIGNS[0] || null; }
    function someone() { return S.who !== 'me'; }
    function steps() { return S.who === 'me' ? ['who', 'amount', 'design', 'review'] : ['who', 'amount', 'design', 'names', 'method', 'message', 'when', 'review']; }
    function questions() { return steps().filter(function (k) { return k !== 'review'; }); }
    function firstUnanswered() {
      var st = steps();
      for (var i = 0; i < st.length; i++) { if (st[i] !== 'review' && !S.answered[st[i]]) return st[i]; }
      return 'review';
    }
    function stepEl(k) { return q('[data-step="' + k + '"]'); }

    /* ---------------- time, in the shop's zone ---------------- */

    function zoneNow() {
      var parts = new Intl.DateTimeFormat('en-US', { timeZone: TZ, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit' }).formatToParts(new Date());
      var get = function (t) { for (var i = 0; i < parts.length; i++) { if (parts[i].type === t) return Number(parts[i].value); } return 0; };
      return { y: get('year'), m: get('month'), d: get('day'), h: get('hour') % 24 };
    }
    function isoOf(y, m, d) { var t = new Date(Date.UTC(y, m - 1, d)); return t.getUTCFullYear() + '-' + pad(t.getUTCMonth() + 1) + '-' + pad(t.getUTCDate()); }
    function isoPlus(days) { var n = zoneNow(); return isoOf(n.y, n.m, n.d + days); }
    function slotPassed(date, hour) { var n = zoneNow(); return !!date && date === isoOf(n.y, n.m, n.d) && hour <= n.h; }
    function firstOpenSlot(date) { for (var i = 0; i < SLOTS.length; i++) { if (!slotPassed(date, SLOTS[i])) return SLOTS[i]; } return null; }

    /* wall-clock date + hour in TZ -> UTC Date (two-pass offset probe, as the app) */
    function offsetMinutes(date) {
      var parts = new Intl.DateTimeFormat('en-US', { timeZone: TZ, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).formatToParts(date);
      var get = function (t) { for (var i = 0; i < parts.length; i++) { if (parts[i].type === t) return Number(parts[i].value); } return 0; };
      var asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
      return Math.round((asUtc - date.getTime()) / 60000);
    }
    function zonedToUtc(dateStr, hour) {
      var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr || '');
      if (!m) return null;
      var guess = Date.UTC(+m[1], +m[2] - 1, +m[3], hour, 0, 0);
      var utc = guess - offsetMinutes(new Date(guess)) * 60000;
      utc = guess - offsetMinutes(new Date(utc)) * 60000;
      return new Date(utc);
    }

    /* ---------------- copy ---------------- */

    function fmtDate(iso) { var p = iso.split('-'); return MONTHS[Number(p[1]) - 1] + ' ' + Number(p[2]); }
    function weekday(iso) { var p = iso.split('-'); return DAYS[new Date(Date.UTC(+p[0], +p[1] - 1, +p[2])).getUTCDay()]; }
    function slotLabel(h) { return (h % 12 || 12) + (h < 12 ? 'am' : 'pm'); }
    function methodText(m) { return m === 'both' ? 'email and text' : m === 'sms' ? 'text' : 'email'; }
    function amountLabel() { var a = amountBy(S.amount); return a ? a.label : ''; }
    function whenLabel() { return fmtDate(S.date) + ' at ' + slotLabel(S.slot) + ' ' + TZ_LABEL; }

    function sendsLine() {
      if (!someone()) return 'emailed to you right after checkout, ready to use.';
      if (S.when !== 'later') return 'sends right away by ' + methodText(S.method) + '.';
      if (!S.date) return 'pick a day and we hold it until then.';
      return 'sends ' + weekday(S.date) + ', ' + whenLabel() + ' by ' + methodText(S.method) + '.';
    }

    function rowValue(k) {
      var d = heroDesign();
      var from = trim(S.from), to = trim(S.to), msg = trim(S.message);
      switch (k) {
        case 'who': return someone() ? 'send it to someone' : 'keep it for myself';
        case 'amount': return amountLabel();
        case 'design': return d ? d.value : '';
        case 'names': return from && to ? 'from ' + from + ', to ' + to : from ? 'from ' + from : to ? 'to ' + to : 'no names';
        case 'method':
          var contacts = [S.method !== 'sms' ? trim(S.email) : '', S.method !== 'email' ? trim(S.mobile) : ''].filter(Boolean).join(', ');
          return methodText(S.method) + (contacts ? ' · ' + contacts : '');
        case 'message': return msg ? '“' + (msg.length > 48 ? trim(msg.slice(0, 48)) + '…' : msg) + '”' : 'no message';
        case 'when': return S.when === 'later' && S.date ? weekday(S.date) + ', ' + whenLabel() : 'right away';
      }
      return '';
    }

    /* ---------------- phone ---------------- */

    function formatPhone(v) {
      v = String(v || '');
      if (/^\s*\+/.test(v)) return '+' + v.replace(/\D/g, '').slice(0, 15);
      var d = v.replace(/\D/g, '');
      if (d.length === 11 && d.charAt(0) === '1') d = d.slice(1);
      d = d.slice(0, 10);
      if (d.length < 4) return d.length ? '(' + d : '';
      if (d.length < 7) return '(' + d.slice(0, 3) + ') ' + d.slice(3);
      return '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6);
    }
    function phoneOk(v) {
      var digits = String(v || '').replace(/\D/g, '');
      if (/^\s*\+/.test(v)) return digits.length >= 8 && digits.length <= 15;
      return digits.length === 10;
    }

    /* ---------------- validation: the three hard requirements ---------------- */

    function validateStep(step) {
      var e = {};
      if (!someone()) return e;
      if (step === 'method') {
        var email = trim(S.email), mobile = trim(S.mobile);
        if (S.method !== 'sms' && !EMAIL_RE.test(email)) e.email = email ? 'that email doesn’t look right' : 'we need their email to send it';
        if (S.method !== 'email' && !phoneOk(mobile)) e.mobile = mobile ? 'that number doesn’t look right' : 'we need their mobile to text it';
      }
      if (step === 'when' && S.when === 'later') {
        if (!S.date) e.date = 'pick a day to send it';
        else if (S.date < isoPlus(0) || S.date > isoPlus(MAX_DAYS)) e.date = 'pick a day within the next ' + MAX_DAYS + ' days';
        else if (firstOpenSlot(S.date) === null) e.date = 'today’s times have passed, pick tomorrow or later';
      }
      return e;
    }

    /* ---------------- render ---------------- */

    function setText(node, text) { if (node && node.textContent !== text) node.textContent = text; }

    function render() {
      var st = steps(), qs = questions();
      var done = qs.filter(function (k) { return S.answered[k]; }).length;
      setText(el.count, done + ' of ' + qs.length);
      if (el.progress) {
        el.progress.setAttribute('aria-valuemax', String(qs.length));
        el.progress.setAttribute('aria-valuenow', String(done));
        el.progress.setAttribute('aria-valuetext', done + ' of ' + qs.length + ' answered');
      }
      if (el.bar) el.bar.style.width = Math.round((done / qs.length) * 100) + '%';
      var amt = amountBy(S.amount);
      setText(el.price, amt ? amt.price : PRICE_RANGE);

      el.steps.forEach(function (node) {
        var k = node.getAttribute('data-step');
        var inFlow = st.indexOf(k) !== -1;
        var active = S.active === k;
        var answered = inFlow && k !== 'review' && !!S.answered[k];
        node.hidden = !(inFlow && (active || answered));
        node.classList.toggle('is-active', active);
        var row = node.querySelector('[data-open]');
        if (row) {
          row.hidden = !answered || active;
          if (answered) fillRow(row, k);
        }
      });

      qa('[data-pick]').forEach(function (b) {
        var k = b.getAttribute('data-pick'), v = b.getAttribute('data-value');
        b.setAttribute('aria-pressed', S[k] === v ? 'true' : 'false');
      });

      qa('[data-need]').forEach(function (n) {
        var need = n.getAttribute('data-need');
        n.hidden = need === 'email' ? S.method === 'sms' : S.method === 'email';
      });

      qa('[data-field]').forEach(function (input) {
        var f = input.getAttribute('data-field');
        if (input !== document.activeElement && input.value !== S[f]) input.value = S[f];
      });
      qa('[data-error]').forEach(function (n) {
        var f = n.getAttribute('data-error');
        var msg = S.errors[f] || '';
        setText(n, msg);
        n.hidden = !msg;
        var input = q('[data-field="' + f + '"]');
        if (input) {
          if (msg) input.setAttribute('aria-invalid', 'true'); else input.removeAttribute('aria-invalid');
        }
      });

      qa('[data-next-label]').forEach(function (b) {
        var step = b.closest('[data-step]').getAttribute('data-step');
        var filled = step === 'names' ? !!(trim(S.from) || trim(S.to)) : !!trim(S.message);
        setText(b, filled ? b.getAttribute('data-next-label') : b.getAttribute('data-skip-label'));
      });

      var len = S.message.length, left = MESSAGE_MAX - len;
      if (el.counter) {
        setText(el.counter, left <= 40 ? left + ' left' : len + '/' + MESSAGE_MAX);
        el.counter.classList.toggle('is-low', left <= 40);
      }

      if (el.later) el.later.hidden = S.when !== 'later';
      var dateInput = q('[data-field="date"]');
      if (dateInput) {
        dateInput.min = isoPlus(0);
        dateInput.max = isoPlus(MAX_DAYS);
      }
      qa('[data-slot]').forEach(function (b) {
        var h = Number(b.getAttribute('data-slot'));
        var off = S.when === 'later' && slotPassed(S.date, h);
        b.disabled = off;
        b.setAttribute('aria-pressed', S.slot === h && !off ? 'true' : 'false');
      });
      var sends = sendsLine();
      setText(el.sends, sends);

      renderGallery();
      renderReview(sends);
      renderSticky();
    }

    function fillRow(row, k) {
      var value = rowValue(k);
      setText(row.querySelector('[data-row-value]'), value);
      row.setAttribute('aria-label', LABELS[k] + ': ' + value + '. change');
      var thumb = row.querySelector('[data-row-thumb]');
      var d = heroDesign();
      if (thumb && d && thumb.getAttribute('src') !== d.thumb) thumb.setAttribute('src', d.thumb);
    }

    function renderGallery() {
      var d = heroDesign();
      el.arts.forEach(function (img) {
        var on = !!d && img.getAttribute('data-art') === d.value;
        if (on) hydrate(img);
        img.classList.toggle('is-on', on);
        if (on) img.removeAttribute('aria-hidden'); else img.setAttribute('aria-hidden', 'true');
      });
      var to = trim(S.to);
      if (el.chipAmount) {
        setText(el.chipAmount, amountLabel());
        el.chipAmount.hidden = !S.amount;
      }
      if (el.chipFor) {
        setText(el.chipFor, 'for ' + to);
        el.chipFor.hidden = !(S.who === 'someone' && to && S.active !== 'who');
      }
    }

    function previewFor() {
      if (!someone()) return 'for you';
      var to = trim(S.to);
      return to ? 'for ' + to : 'for them';
    }

    function galleryArt(d) {
      for (var i = 0; i < el.arts.length; i++) { if (el.arts[i].getAttribute('data-art') === d.value) return el.arts[i]; }
      return null;
    }

    function renderReview(sends) {
      var d = heroDesign();
      if (el.reviewArt && d) {
        var shown = galleryArt(d);
        var src = shown && shown.complete && shown.currentSrc ? shown.currentSrc : d.src;
        if (el.reviewArt.getAttribute('src') !== src) {
          el.reviewArt.setAttribute('src', src);
          el.reviewArt.removeAttribute('srcset');
        }
      }
      setText(el.reviewFor, previewFor());
      setText(el.reviewAmount, amountLabel());
      setText(el.backFor, previewFor());
      var msg = trim(S.message);
      var text = someone() ? (msg ? '“' + msg + '”' : 'your message will sit here, in your words.') : SELF_NOTE;
      setText(el.backMsg, text);
      if (el.backMsg) {
        el.backMsg.classList.toggle('is-placeholder', someone() && !msg);
        el.backMsg.classList.toggle('is-long', someone() && msg.length > 140);
        el.backMsg.classList.toggle('is-mid', someone() && msg.length > 80 && msg.length <= 140);
      }
      var from = trim(S.from);
      setText(el.backFrom, someone() ? (from ? 'from ' + from : 'from you') : 'from you, to you');
      setText(el.backAmount, amountLabel());
      setText(el.backSends, sends);
      setText(el.reviewSends, sends);
      if (el.flip) el.flip.setAttribute('aria-pressed', S.flipped ? 'true' : 'false');
      setText(el.flipHint, S.flipped ? 'tap to see the front' : 'tap to turn it over');
    }

    function renderSticky() {
      if (!el.sticky) return;
      var d = heroDesign();
      setText(el.stickySub, (S.amount ? amountLabel() + ' · ' : '') + (d ? d.value : ''));
      if (el.stickyArt && d && el.stickyArt.getAttribute('src') !== d.thumb) el.stickyArt.setAttribute('src', d.thumb);
      el.sticky.hidden = !(S.active === 'review' && !ctaInView);
    }

    function hydrate(img) {
      if (img.getAttribute('data-srcset')) { img.setAttribute('srcset', img.getAttribute('data-srcset')); img.removeAttribute('data-srcset'); }
      if (img.getAttribute('data-src')) { img.setAttribute('src', img.getAttribute('data-src')); img.removeAttribute('data-src'); }
    }

    /* ---------------- flow ---------------- */

    function headerOffset() {
      var header = document.querySelector('.site-header--stuck, [data-sticky="true"]');
      if (!header) return 0;
      var r = header.getBoundingClientRect();
      return r.bottom > 0 && r.bottom < 160 ? r.bottom : 0;
    }

    function reveal(node) {
      if (!node) return;
      var r = node.getBoundingClientRect();
      var top = headerOffset();
      if (r.top < top + 8 || r.top > window.innerHeight - 200) {
        var y = window.pageYOffset + r.top - top - 24;
        try { window.scrollTo({ top: Math.max(0, y), behavior: reduceMotion ? 'auto' : 'smooth' }); } catch (e) { window.scrollTo(0, Math.max(0, y)); }
      }
    }

    function focusActive() {
      var node = stepEl(S.active);
      if (!node) return;
      var target = null;
      if (finePointer && S.active === 'names') target = q('[data-field="from"]');
      if (finePointer && S.active === 'message') target = q('[data-field="message"]');
      if (!target) target = node.querySelector('.pwt-gc__qtitle');
      if (target) { try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); } }
    }

    function activate() {
      var focusInside = root.contains(document.activeElement);
      if (S.active === 'amount' || S.active === 'design') qa('[data-gc-lazy]').forEach(hydrate);
      render();
      var node = stepEl(S.active);
      var panel = node && node.querySelector('[data-q]');
      if (panel && !reduceMotion) {
        panel.classList.remove('is-revealing');
        void panel.offsetWidth;
        panel.classList.add('is-revealing');
        setTimeout(function () { panel.classList.remove('is-revealing'); }, 450);
      }
      reveal(node);
      if (focusInside) focusActive();
      if (S.active === 'review' && !S.turned && someone() && trim(S.message) && !reduceMotion) {
        S.turned = true;
        setTimeout(function () {
          if (S.active !== 'review' || S.flipped) return;
          S.flipped = true;
          render();
        }, 650);
      }
    }

    function answer(step) {
      clearTimeout(advTimer);
      var errors = validateStep(step);
      if (hasKeys(errors)) {
        S.errors = errors;
        render();
        var bad = q('[aria-invalid="true"]');
        if (bad) { try { bad.focus({ preventScroll: true }); } catch (e) { bad.focus(); } reveal(bad.closest('.pwt-gc__field') || bad); }
        return;
      }
      if (step === 'when' && S.when === 'later' && slotPassed(S.date, S.slot)) S.slot = firstOpenSlot(S.date);
      S.errors = {};
      S.answered[step] = true;
      S.active = firstUnanswered();
      activate();
    }

    function openStep(step) {
      clearTimeout(advTimer);
      S.errors = {};
      S.active = step;
      activate();
    }

    function pick(key, value) {
      clearTimeout(advTimer);
      if (key === 'method' && !OFFER_TEXT) value = 'email';
      S[key] = value;
      if (key === 'method') { S.errors.email = null; S.errors.mobile = null; }
      if (key === 'when') S.errors.date = null;
      render();
      var advance = key === 'who' || key === 'amount' || key === 'design' || (key === 'when' && value === 'now');
      if (advance) {
        advTimer = setTimeout(function () { answer(key); }, ADVANCE_MS);
      } else if (key === 'when' && value === 'later' && finePointer) {
        var dateInput = q('[data-field="date"]');
        if (dateInput && !S.date) { try { dateInput.focus({ preventScroll: true }); } catch (e) { dateInput.focus(); } }
      }
    }

    function pickSlot(hour) {
      if (S.when === 'later' && slotPassed(S.date, hour)) return;
      S.slot = hour;
      S.errors.date = null;
      render();
    }

    function setField(input) {
      var f = input.getAttribute('data-field');
      var v = input.value;
      if (f === 'mobile') {
        var formatted = formatPhone(v);
        if (formatted !== v) input.value = formatted;
        v = formatted;
      }
      if (f === 'message' && v.length > MESSAGE_MAX) { v = v.slice(0, MESSAGE_MAX); input.value = v; }
      S[f] = v;
      if (S.errors[f]) S.errors[f] = null;
      if (f === 'date' && v && slotPassed(v, S.slot)) {
        var open = firstOpenSlot(v);
        if (open !== null) S.slot = open;
      }
      render();
    }

    /* ---------------- add to bag ---------------- */

    function randomId() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 8); }

    function buildProperties(d) {
      var p = {};
      if (!someone()) {
        p['Delivery method'] = 'emailed to you after checkout';
        p._gift_for = 'self';
        p._gift_delivery = 'email';
        p._gift_when = 'after checkout';
      } else {
        var from = trim(S.from), to = trim(S.to), msg = trim(S.message);
        if (to) p['Recipient name'] = to;
        if (from) p['From'] = from;
        if (S.method !== 'sms') p['Recipient email'] = trim(S.email).toLowerCase();
        if (S.method !== 'email') p['Recipient phone'] = trim(S.mobile);
        if (msg) p['Gift message'] = msg;
        p['Delivery method'] = methodText(S.method);
        if (S.when === 'later' && S.date) {
          var at = zonedToUtc(S.date, S.slot);
          p['Delivery date'] = fmtDate(S.date) + ', ' + S.date.slice(0, 4) + ' at ' + slotLabel(S.slot) + ' ' + TZ_LABEL;
          if (at) p._gift_send_at = at.toISOString();
          p._gift_when = whenLabel();
        } else {
          p._gift_when = 'right away';
        }
        p._gift_for = 'someone';
        p._gift_delivery = S.method;
      }
      p._gift_tz = TZ;
      p._gift_design = d ? d.slug : '';
      p._gift_id = randomId();
      return p;
    }

    function setBusy(state, added) {
      busy = state;
      el.adds.forEach(function (b) {
        if (state) {
          b.setAttribute('aria-busy', 'true');
          b.textContent = 'adding…';
        } else {
          b.removeAttribute('aria-busy');
          b.textContent = added ? 'added' : 'add to bag';
        }
      });
      if (!state && added) {
        setTimeout(function () { if (!busy) el.adds.forEach(function (b) { b.textContent = 'add to bag'; }); }, 1600);
      }
    }

    function showAddError(msg) {
      if (!el.addError) return;
      setText(el.addError, msg || '');
      el.addError.hidden = !msg;
    }

    function addToBag() {
      if (busy) return;
      showAddError('');
      clearTimeout(advTimer);
      if (!S.who || !S.amount || !S.design) {
        S.active = !S.who ? 'who' : !S.amount ? 'amount' : 'design';
        activate();
        return;
      }
      var errors = {};
      var em = validateStep('method'), wh = validateStep('when');
      for (var k in em) errors[k] = em[k];
      for (var j in wh) errors[j] = wh[j];
      if (hasKeys(errors)) {
        S.errors = errors;
        S.active = errors.email || errors.mobile ? 'method' : 'when';
        activate();
        return;
      }
      if (someone() && S.when === 'later' && slotPassed(S.date, S.slot)) S.slot = firstOpenSlot(S.date);
      var variant = variantFor(S.amount, S.design);
      if (!variant || !variant.available) {
        showAddError('that amount and design is not available right now. try another design.');
        return;
      }
      var properties = buildProperties(heroDesign());
      setBusy(true);

      if (window.NativeCart && typeof window.NativeCart.add === 'function') {
        Promise.resolve(window.NativeCart.add(variant.id, 1, properties)).then(
          function () { setBusy(false, true); },
          function () { setBusy(false, false); showAddError('something went wrong. please try again.'); }
        );
        return;
      }
      var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(rootUrl + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1, properties: properties }] })
      }).then(function (r) {
        if (!r.ok) throw new Error('add failed');
        window.location.href = rootUrl + 'cart';
      }).catch(function () {
        setBusy(false, false);
        showAddError('something went wrong. please try again.');
      });
    }

    /* ---------------- events ---------------- */

    if (el.slots) {
      el.slots.innerHTML = SLOTS.map(function (h) {
        return '<button type="button" class="pwt-gc__slot" data-slot="' + h + '" aria-pressed="false">' + slotLabel(h) + '</button>';
      }).join('');
    }

    root.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target.closest('[data-pick],[data-slot],[data-next],[data-open],[data-gc-flip],[data-gc-add]') : null;
      if (!t || !root.contains(t)) return;
      if (t.hasAttribute('data-pick')) { pick(t.getAttribute('data-pick'), t.getAttribute('data-value')); return; }
      if (t.hasAttribute('data-slot')) { pickSlot(Number(t.getAttribute('data-slot'))); return; }
      if (t.hasAttribute('data-next')) { answer(S.active); return; }
      if (t.hasAttribute('data-open')) { openStep(t.getAttribute('data-open')); return; }
      if (t.hasAttribute('data-gc-flip')) { S.flipped = !S.flipped; render(); return; }
      if (t.hasAttribute('data-gc-add')) { addToBag(); }
    });

    function onField(e) {
      var t = e.target;
      if (t && t.getAttribute && t.getAttribute('data-field')) setField(t);
    }
    root.addEventListener('input', onField);
    root.addEventListener('change', onField);

    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' || e.isComposing) return;
      var t = e.target;
      if (!t || t.tagName !== 'INPUT' || !t.getAttribute('data-field')) return;
      e.preventDefault();
      setField(t);
      answer(S.active);
    });

    if (el.cta && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        ctaInView = entries[entries.length - 1].isIntersecting;
        renderSticky();
      }, { rootMargin: '-8px 0px -8px 0px' }).observe(el.cta);
    }

    function hydrateAll() { el.arts.forEach(hydrate); qa('[data-gc-lazy]').forEach(hydrate); }
    function idle(fn) { (window.requestIdleCallback || function (cb) { return setTimeout(cb, 400); })(fn); }
    if (document.readyState === 'complete') idle(hydrateAll);
    else window.addEventListener('load', function () { idle(hydrateAll); });

    render();
  }

  function boot() {
    var roots = document.querySelectorAll(ROOT_SEL);
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
